import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { TextInput, SelectInput } from "@/components/ui/Field";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { ApiError } from "@/lib/apiClient";
import {
  createPersonnelCostCategory,
  deletePersonnelCostCategory,
  departmentEntries,
  employeeEntries,
  globalEntries,
  upsertPersonnelCostEntries,
  type PersonnelCostCategory,
  type PersonnelCostDepartment,
  type PersonnelCostEntry,
  type PersonnelCostEntryUpsert,
  type PersonnelCostValueKind,
} from "@/lib/personnelCosts";
import { toast } from "@/lib/toast";
import type { EmployeeOption } from "@/pages/personal/personalkosten/PersonalkostenPage";
import {
  DepartmentMultiSelect,
} from "@/pages/personal/personalkosten/DepartmentMultiSelect";
import {
  formatAmountInput,
  MonthYearAmountFields,
  monthToYear,
} from "@/pages/personal/personalkosten/MonthYearAmountFields";

type CostScopeTab = "global" | "employee";
type EmployeeCostMode = "single" | "department";

interface CategoryDraft {
  amount_monthly: string;
  amount_yearly: string;
  percent_value: string;
  hourly_amount: string;
  note: string;
  departments: PersonnelCostDepartment[];
}

interface CostEntriesPanelProps {
  employees: EmployeeOption[];
  categories: PersonnelCostCategory[];
  entries: PersonnelCostEntry[];
  selectedLogin: string | null;
  onSelectedLoginChange: (login: string) => void;
  onRefresh: () => void;
}

function emptyDraft(): CategoryDraft {
  return {
    amount_monthly: "",
    amount_yearly: "",
    percent_value: "",
    hourly_amount: "",
    note: "",
    departments: [],
  };
}

function entryToDraft(entry: PersonnelCostEntry | undefined, category: PersonnelCostCategory): CategoryDraft {
  if (!entry) {
    if (category.value_kind === "percent_gross" && category.default_percent != null) {
      return { ...emptyDraft(), percent_value: String(category.default_percent) };
    }
    return emptyDraft();
  }
  const monthly = Number(entry.amount_monthly) || 0;
  return {
    amount_monthly: monthly ? String(monthly) : "",
    amount_yearly: monthly ? formatAmountInput(monthToYear(monthly)) : "",
    percent_value: entry.percent_value != null ? String(entry.percent_value) : "",
    hourly_amount: entry.hourly_amount != null ? String(entry.hourly_amount) : "",
    note: entry.note ?? "",
    departments: entry.departments ?? [],
  };
}

export function CostEntriesPanel({
  employees,
  categories,
  entries,
  selectedLogin,
  onSelectedLoginChange,
  onRefresh,
}: CostEntriesPanelProps) {
  const [scopeTab, setScopeTab] = useState<CostScopeTab>("global");
  const [employeeCostMode, setEmployeeCostMode] = useState<EmployeeCostMode>("single");
  const [drafts, setDrafts] = useState<Record<number, CategoryDraft>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newLabel, setNewLabel] = useState("");
  const [newValueKind, setNewValueKind] = useState<PersonnelCostValueKind>("monthly_eur");
  const [creating, setCreating] = useState(false);

  const scopedEntries = useMemo(() => {
    if (scopeTab === "global") return globalEntries(entries);
    if (employeeCostMode === "department") return departmentEntries(entries);
    if (!selectedLogin) return [];
    return employeeEntries(entries, selectedLogin);
  }, [entries, scopeTab, employeeCostMode, selectedLogin]);

  const entryByCategory = useMemo(() => {
    const map = new Map<number, PersonnelCostEntry>();
    for (const entry of scopedEntries) {
      map.set(entry.category_id, entry);
    }
    return map;
  }, [scopedEntries]);

  useEffect(() => {
    const next: Record<number, CategoryDraft> = {};
    for (const category of categories) {
      next[category.id] = entryToDraft(entryByCategory.get(category.id), category);
    }
    setDrafts(next);
  }, [categories, entryByCategory]);

  const updateDraft = useCallback((categoryId: number, patch: Partial<CategoryDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [categoryId]: { ...(prev[categoryId] ?? emptyDraft()), ...patch },
    }));
  }, []);

  async function handleSave() {
    if (scopeTab === "employee" && employeeCostMode === "single" && !selectedLogin) {
      setError("Bitte einen Mitarbeiter auswählen.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const scope =
        scopeTab === "global" ? "global" : employeeCostMode === "department" ? "department" : "employee";

      const payload: PersonnelCostEntryUpsert[] = categories.map((category) => {
        const draft = drafts[category.id] ?? emptyDraft();
        const base: PersonnelCostEntryUpsert = {
          category_id: category.id,
          scope,
          user_login: scope === "employee" ? selectedLogin : null,
          departments:
            scope === "global"
              ? draft.departments.length ? draft.departments : null
              : scope === "department"
                ? draft.departments
                : null,
          note: draft.note.trim() || null,
        };

        if (category.value_kind === "percent_gross") {
          return { ...base, percent_value: Number(draft.percent_value) || 0 };
        }
        if (category.value_kind === "hourly_eur") {
          return { ...base, hourly_amount: Number(draft.hourly_amount) || 0 };
        }
        return { ...base, amount_monthly: Number(draft.amount_monthly) || 0 };
      });

      if (scope === "department") {
        const missingDepartments = payload.some((item) => !item.departments?.length);
        if (missingDepartments) {
          setError("Bitte für Abteilungs-Kosten mindestens eine Abteilung auswählen.");
          setSaving(false);
          return;
        }
      }

      await upsertPersonnelCostEntries(payload);
      toast.success("Kosten gespeichert.");
      onRefresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateCategory() {
    const label = newLabel.trim();
    if (!label) {
      setError("Bezeichnung für neue Kostenposition erforderlich.");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await createPersonnelCostCategory({ label, value_kind: newValueKind });
      setNewLabel("");
      toast.success("Kostenposition angelegt.");
      onRefresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kostenposition konnte nicht angelegt werden.");
    } finally {
      setCreating(false);
    }
  }

  async function handleDeleteCategory(category: PersonnelCostCategory) {
    if (category.is_system) return;
    if (!window.confirm(`Kostenposition „${category.label}" wirklich löschen?`)) return;
    setError(null);
    try {
      await deletePersonnelCostCategory(category.id);
      toast.success("Kostenposition gelöscht.");
      onRefresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  const description =
    scopeTab === "global"
      ? "Gesamtbeträge werden gleichmäßig auf die gewählten Abteilungen verteilt. Ohne Auswahl gelten alle Abteilungen."
      : employeeCostMode === "department"
        ? "Betrag gilt pro Mitarbeiter in den gewählten Abteilungen – z. B. Microsoft-Lizenzen je Platz."
        : "Individuelle Kosten nur für den ausgewählten Mitarbeiter.";

  return (
    <div className="space-y-4">
      <Card
        title="Kosten erfassen"
        description={description}
        actions={
          <Button type="button" onClick={() => void handleSave()} disabled={saving}>
            Speichern
          </Button>
        }
      >
        <div className="mb-4 flex flex-wrap items-end gap-4">
          <SegmentedControl
            value={scopeTab}
            onChange={setScopeTab}
            name="Kosten-Bereich"
            options={[
              { value: "global", label: "Globale Kosten" },
              { value: "employee", label: "Mitarbeiter-Kosten" },
            ]}
          />
          {scopeTab === "employee" ? (
            <>
              <SegmentedControl
                value={employeeCostMode}
                onChange={setEmployeeCostMode}
                name="Mitarbeiter-Kosten-Modus"
                options={[
                  { value: "single", label: "Einzelner Mitarbeiter" },
                  { value: "department", label: "Abteilung(en)" },
                ]}
              />
              {employeeCostMode === "single" ? (
                <div className="min-w-[220px] flex-1">
                  <SelectInput
                    label="Mitarbeiter"
                    value={selectedLogin ?? ""}
                    onChange={(e) => onSelectedLoginChange(e.target.value)}
                  >
                    <option value="">Bitte wählen …</option>
                    {employees.map((emp) => (
                      <option key={emp.login} value={emp.login}>
                        {emp.display}
                      </option>
                    ))}
                  </SelectInput>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        {error ? <Alert variant="error" className="mb-4">{error}</Alert> : null}

        <div className="space-y-3">
          {categories.map((category) => {
            const draft = drafts[category.id] ?? emptyDraft();
            const showDepartments =
              scopeTab === "global" ||
              (scopeTab === "employee" && employeeCostMode === "department");

            return (
              <div
                key={category.id}
                className="grid gap-3 rounded-lg border border-separator bg-card-elevated p-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-label">{category.label}</p>
                    <p className="text-xs text-label-secondary">
                      {category.value_kind === "percent_gross"
                        ? "Prozent vom Bruttogehalt"
                        : category.value_kind === "hourly_eur"
                          ? "Betrag pro Stunde"
                          : scopeTab === "global"
                            ? "Gesamtbetrag (wird verteilt)"
                            : employeeCostMode === "department"
                              ? "Betrag pro Mitarbeiter in Abteilung"
                              : "Betrag für einen Mitarbeiter"}
                      {category.is_system ? " · Standard" : " · Eigene Position"}
                    </p>
                  </div>
                  {!category.is_system ? (
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => void handleDeleteCategory(category)}
                    >
                      Entfernen
                    </Button>
                  ) : null}
                </div>

                <div className="grid gap-3 xl:grid-cols-[auto_1fr_auto]">
                  {category.value_kind === "percent_gross" ? (
                    <div className="min-w-[160px]">
                      <TextInput
                        label="Prozent"
                        type="number"
                        min={0}
                        step={0.1}
                        value={draft.percent_value}
                        onChange={(e) => updateDraft(category.id, { percent_value: e.target.value })}
                      />
                    </div>
                  ) : category.value_kind === "hourly_eur" ? (
                    <div className="min-w-[160px]">
                      <TextInput
                        label="€/h"
                        type="number"
                        min={0}
                        step={0.01}
                        value={draft.hourly_amount}
                        onChange={(e) => updateDraft(category.id, { hourly_amount: e.target.value })}
                      />
                    </div>
                  ) : (
                    <div className="min-w-[280px]">
                      <MonthYearAmountFields
                        monthly={draft.amount_monthly}
                        yearly={draft.amount_yearly}
                        onMonthlyChange={(value) => updateDraft(category.id, { amount_monthly: value })}
                        onYearlyChange={(value) => updateDraft(category.id, { amount_yearly: value })}
                      />
                    </div>
                  )}

                  {showDepartments ? (
                    <DepartmentMultiSelect
                      value={draft.departments}
                      onChange={(departments) => updateDraft(category.id, { departments })}
                      allowEmpty={scopeTab === "global"}
                      emptyLabel="Alle Abteilungen"
                      hint={
                        scopeTab === "global"
                          ? "Leer = alle Mitarbeiter. Auswahl = nur diese Abteilungen."
                          : "Mindestens eine Abteilung wählen."
                      }
                    />
                  ) : (
                    <div />
                  )}

                  <TextInput
                    label="Notiz"
                    value={draft.note}
                    onChange={(e) => updateDraft(category.id, { note: e.target.value })}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card title="Eigene Kostenposition" description="Zusätzliche laufende Kosten über die Standard-Kategorien hinaus.">
        <div className="grid gap-3 sm:grid-cols-[1fr_180px_auto]">
          <TextInput
            label="Bezeichnung"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="z. B. Microsoft 365 Lizenz"
          />
          <SelectInput
            label="Art"
            value={newValueKind}
            onChange={(e) => setNewValueKind(e.target.value as PersonnelCostValueKind)}
          >
            <option value="monthly_eur">Monatlicher Betrag</option>
            <option value="percent_gross">Prozent vom Brutto</option>
            <option value="hourly_eur">Stundensatz</option>
          </SelectInput>
          <div className="flex items-end">
            <Button type="button" variant="secondary" onClick={() => void handleCreateCategory()} disabled={creating}>
              Hinzufügen
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
